import { Component, OnInit, ViewChildren, QueryList, AfterViewInit, ElementRef, ViewChild, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { Select, Store, Actions } from '@ngxs/store';
import { ChapterModel } from 'src/app/story/models/chapter.model';
import { Observable, Subject } from 'rxjs';
import { CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
import { MatSnackBar } from '@angular/material';
import { ChapterCardComponent } from '../chapter-card/chapter-card.component';
import { filter, map, takeUntil } from 'rxjs/operators';
import { 
  SelectChapter,
  SetChapters,
  NextChapter,
  PreviousChapter,
  RemoveChapter, 
  DuplicateChapter
} from 'src/app/story/state/chapters/story.chapters.actions';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { StoryChapterThumbnail } from 'src/app/story/models/story-chapter-thumbnail.model';
import { DialogService } from 'src/app/dialogs/dialog.service';
import { ImportStory } from 'src/app/story/state/story.actions';
import { BookmarkModel } from 'src/app/bookmarks/models/bookmark.model';


@Component({
  selector: 'app-chapter-browser',
  templateUrl: './chapter-browser.component.html',
  styleUrls: ['./chapter-browser.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChapterBrowserComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('chapterBrowser')
  chapterBrowserElement: ElementRef;

  @ViewChildren(ChapterCardComponent)
  chapterCards: QueryList<ChapterCardComponent>;

  @Select(StoryChaptersState.getStoryChapters)
  chapters$: Observable<ChapterModel[]>;

  @Select(StoryChaptersState.getSelectedChapter)
  selectedChapter$: Observable<ChapterModel>;

  @Select(StoryChaptersState.getRegisteredThumbnails)
  thumbnails$: Observable<Map<string, StoryChapterThumbnail>>;
  
  allChaptersLoaded = false;

  private ngUnsubscribe = new Subject();
  private currentChapterId = '';
  constructor(
    private store: Store,
    private dialogService: DialogService,
    private snackbar: MatSnackBar
  ) { }

  ngOnInit() {
    this.allChaptersLoaded = this.store.selectSnapshot(StoryChaptersState.getStoryChapters)
      .every((chapter) => chapter.readyState.model);
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  ngAfterViewInit() {
    this.selectedChapter$
      .pipe(
        filter(
          selectedChapter => selectedChapter != null
        ),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((selectedChapter) => {
        this.currentChapterId = selectedChapter.id;
        setTimeout(() => {
          this.scrollChapterIntoView(selectedChapter);
        });
      });

    this.chapters$
      .pipe(
        filter(chapters => chapters != null && chapters.length > 0),
        map(chapters => {
          return chapters.every(chapter => chapter.readyState.model === true);
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(allChaptersLoaded => {
        this.allChaptersLoaded = allChaptersLoaded;
      });
  }

  handleDrop(event: CdkDragDrop<ChapterModel[]>) {
    const chapters = this.store.selectSnapshot(StoryChaptersState.getStoryChapters);
    moveItemInArray(chapters, event.previousIndex, event.currentIndex);

    this.store.dispatch(new SetChapters(chapters));
  }

  handleClickChapter(chapter) {
    if (chapter.id !== this.currentChapterId) {
      this.store.dispatch(new SelectChapter(chapter));
    }
  }

  handleImportChapters() {
    const dialogRef = this.dialogService.openImportDialog();

    dialogRef.afterClosed()
      .pipe(
        filter(bookmark => bookmark != null)
      )
      .subscribe((bookmark: BookmarkModel) => {
        this.store.dispatch(new ImportStory(bookmark.id, bookmark.type));
      });
  }

  handleNextClick() {
    this.store.dispatch(new NextChapter());
  }

  handlePreviousClick() {
    this.store.dispatch(new PreviousChapter());
  }

  handleDuplicate(chapter: ChapterModel) {
    this.duplicateChapter(chapter);
  }

  handleDuplicateSelected() {
    const chapter = this.store.selectSnapshot(StoryChaptersState.getSelectedChapter);
    this.duplicateChapter(chapter);
  }

  handleRemove(chapter: ChapterModel) {
    this.removeChapter(chapter);
  }

  duplicateChapter(chapter: ChapterModel) {
    // exact copy of current chapter
    this.store.dispatch(new DuplicateChapter(chapter));
    this.snackbar.open(`Duplicated chapter "${chapter.info.displayName}"`);
  }

  removeChapter(chapter: ChapterModel) {
    const dialogRef = this.dialogService.openConfirmDialog({
      data: {
        titleText: 'Remove chapter',
        bodyText: 'This cannot be undone.',
        confirmText: 'Remove',
        confirmColor: 'warn'
      }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.store.dispatch(new RemoveChapter(chapter));
        this.snackbar.open(`"${chapter.info.displayName}" chapter has been removed`);
      }
    });
  }

  scrollChapterIntoView(chapter: ChapterModel) {
    const target = document.getElementById(`chapter-card__${chapter.id}`);
    const isSmoothScrollSupported = 'scrollBehavior' in document.documentElement.style;
    if (isSmoothScrollSupported && target) {
      target.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }
}
